import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import { 
  User, 
  Mail, 
  Phone,
  Building,
  Briefcase,
  Calendar,
  Edit,
  Save,
  X,
  Camera,
  Shield
} from "lucide-react";
import { userApi, UserInfoData } from "@/api/modules/user";

const Profile = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<UserInfoData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    nickname: "",
    email: "",
    phone: "",
    company: "",
    profession: ""
  });

  // 获取用户信息
  // useEffect(() => {
  //   const fetchUserInfo = async () => {
  //     const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  //
  //     if (!token) {
  //       navigate('/');
  //       return;
  //     }
  //
  //     try {
  //       const response = await userApi.getUserDetail();
  //       if (response?.data) {
  //         setUserInfo(response.data);
  //         setFormData({
  //           username: response.data.username || "",
  //           nickname: response.data.nickname || "",
  //           email: response.data.email || "",
  //           phone: response.data.phone || "",
  //           company: response.data.company || "",
  //           profession: response.data.profession || ""
  //         });
  //       }
  //     } catch (error) {
  //       console.error('获取用户信息失败:', error);
  //       // 尝试从 localStorage 读取缓存
  //       const cachedUserInfo = localStorage.getItem('userInfo');
  //       if (cachedUserInfo) {
  //         const parsed = JSON.parse(cachedUserInfo);
  //         setUserInfo(parsed);
  //         setFormData({
  //           username: parsed.username || "",
  //           nickname: parsed.nickname || "",
  //           email: parsed.email || "",
  //           phone: parsed.phone || "",
  //           company: parsed.company || "",
  //           profession: parsed.profession || ""
  //         });
  //       }
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };
  //
  //   fetchUserInfo();
  // }, [navigate]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // TODO: 调用更新用户信息接口
      // const response = await userApi.updateUser(userInfo.id, formData);
      
      // 模拟保存成功
      setUserInfo({ ...userInfo, ...formData });
      localStorage.setItem('userInfo', JSON.stringify({ ...userInfo, ...formData }));
      setIsEditing(false);
    } catch (error) {
      console.error('保存失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // 恢复原始数据
    if (userInfo) {
      setFormData({
        username: userInfo.username || "",
        nickname: userInfo.nickname || "",
        email: userInfo.email || "",
        phone: userInfo.phone || "",
        company: userInfo.company || "",
        profession: userInfo.profession || ""
      });
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">加载中...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            个人信息
          </h1>
          <p className="text-xl text-gray-600">
            管理您的账户信息和偏好设置
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* 左侧 - 用户头像和基本信息 */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center">
                    <div className="relative">
                      <Avatar className="h-32 w-32">
                        <AvatarImage src={userInfo?.avatar} alt={userInfo?.nickname || userInfo?.username} />
                        <AvatarFallback className="text-3xl bg-blue-100 text-blue-600">
                          {(userInfo?.nickname || userInfo?.username || 'U').charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <Button
                        size="sm"
                        className="absolute bottom-0 right-0 rounded-full h-10 w-10 p-0"
                        disabled
                      >
                        <Camera className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <h2 className="mt-4 text-2xl font-bold text-gray-900">
                      {userInfo?.nickname || userInfo?.username}
                    </h2>
                    
                    <Badge variant="secondary" className="mt-2">
                      普通用户
                    </Badge>

                    <div className="w-full mt-6 space-y-3 text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4"/>
                        <span>注册时间：2024年1月</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4"/>
                        <span>最近登录：{userInfo?.lastLoginTime}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Shield className="h-4 w-4"/>
                        <span>账户状态：正常</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* 右侧 - 详细信息 */}
            <div className="lg:col-span-3">
            <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>账户信息</CardTitle>
                      <CardDescription>查看和编辑您的个人信息</CardDescription>
                    </div>
                    {!isEditing ? (
                      <Button onClick={() => setIsEditing(true)} variant="outline">
                        <Edit className="mr-2 h-4 w-4" />
                        编辑
                      </Button>
                    ) : (
                      <div className="flex space-x-2">
                        <Button onClick={handleCancel} variant="outline" disabled={isSaving}>
                          <X className="mr-2 h-4 w-4" />
                          取消
                        </Button>
                        <Button onClick={handleSave} disabled={isSaving}>
                          <Save className="mr-2 h-4 w-4" />
                          {isSaving ? '保存中...' : '保存'}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* 用户名 */}
                    <div>
                      <Label htmlFor="username" className="flex items-center space-x-2 mb-2">
                        <User className="w-4 h-4"/>
                        <span>用户名</span>
                      </Label>
                      <Input
                          id="username"
                          value={formData.username}
                          onChange={(e) => handleInputChange("username", e.target.value)}
                          disabled
                          className={!isEditing ? "bg-gray-50" : ""}
                      />
                      <p className="text-xs text-gray-500 mt-1">用户名不可修改</p>
                    </div>

                    {/* 昵称 */}
                    <div>
                      <Label htmlFor="nickname" className="flex items-center space-x-2 mb-2">
                        <User className="w-4 h-4"/>
                        <span>昵称</span>
                      </Label>
                      <Input
                        id="nickname"
                        value={formData.nickname}
                        onChange={(e) => handleInputChange("nickname", e.target.value)}
                        disabled={!isEditing}
                        className={!isEditing ? "bg-gray-50" : ""}
                      />
                    </div>

                    {/* 邮箱 */}
                    <div>
                      <Label htmlFor="email" className="flex items-center space-x-2 mb-2">
                        <Mail className="w-4 h-4"/>
                        <span>邮箱</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        disabled={!isEditing}
                        className={!isEditing ? "bg-gray-50" : ""}
                      />
                    </div>

                    {/* 手机号 */}
                    <div>
                      <Label htmlFor="phone" className="flex items-center space-x-2 mb-2">
                        <Phone className="w-4 h-4"/>
                        <span>手机号</span>
                      </Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        disabled
                        className="bg-gray-50"
                      />
                      <p className="text-xs text-gray-500 mt-1">手机号不可修改</p>
                    </div>

                    {/* 公司 */}
                    <div>
                      <Label htmlFor="company" className="flex items-center space-x-2 mb-2">
                        <Building className="w-4 h-4"/>
                        <span>公司</span>
                      </Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => handleInputChange("company", e.target.value)}
                        disabled={!isEditing}
                        className={!isEditing ? "bg-gray-50" : ""}
                      />
                    </div>

                    {/* 职业 */}
                    <div>
                      <Label htmlFor="profession" className="flex items-center space-x-2 mb-2">
                        <Briefcase className="w-4 h-4"/>
                        <span>职业</span>
                      </Label>
                      <Input
                        id="profession"
                        value={formData.profession}
                        onChange={(e) => handleInputChange("profession", e.target.value)}
                        disabled={!isEditing}
                        className={!isEditing ? "bg-gray-50" : ""}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Profile;
